import React, { useState, useEffect } from "react";
import AskedOfYou from "../../components/AskedOfYou.jsx";
import Wordmark from "../../components/Wordmark.jsx";
import { Link, useNavigate } from "react-router-dom";
import UserChip from "../../components/UserChip.jsx";
import ClientNameBlock from "../../components/ClientNameBlock.jsx";
import { useAuth } from "../../hooks/useAuth.jsx";

function Topbar({ user, onLogout }) {
  return (
    <div style={tb.bar}>
      <Wordmark />
      <div style={tb.right}>
        <UserChip user={user} onLogout={onLogout} />
      </div>
    </div>
  );
}

export default function CaregiverClients() {
  const { user, logout } = useAuth();
  const navigate          = useNavigate();
  const [clients, setClients] = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetch("/api/caregiver/clients")
      .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(setClients)
      .catch(e => setError(e.message));
  }, []);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const first = user?.display_name?.split(" ")[0] || "there";

  // null means still loading; an empty array means genuinely nobody yet, and
  // the two must not look alike. A DSP hired this morning and not yet assigned
  // anyone previously saw "You are connected with 0 clients" over a blank grid
  // — a count where an explanation belonged.
  const isEmpty = clients !== null && clients.length === 0;

  return (
    <div style={styles.page}>
      <Topbar user={user} onLogout={handleLogout} />
      <div style={styles.container}>
        <h1 style={styles.heading}>Welcome, {first}</h1>
        <AskedOfYou />
        {clients && !isEmpty && (
          <p style={styles.subtitle}>
            You are connected with {clients.length} {clients.length === 1 ? "client" : "clients"}.
          </p>
        )}

        {error && <p style={{ color: "var(--color-critical)", fontSize: "var(--text-sm)" }}>{error}</p>}

        {/* No action offered, because the DSP has none to take. Waiting is the
            correct state; saying so is the fix. */}
        {isEmpty && (
          <div style={styles.empty}>
            <p style={styles.emptyTitle}>Nothing here yet</p>
            <p style={styles.emptyBody}>
              Your coordinator will add the people you'll be supporting. You'll
              see them here when they do.
            </p>
          </div>
        )}

        <div style={styles.grid}>
          {(clients || []).map(client => {
            const meta = [client.age && `${client.age}`, client.location].filter(Boolean).join(" · ");
            return (
              <Link key={client.id} to={`/clients/${client.id}`} style={styles.card}>
                <div style={styles.cardHead}>
                  <div style={styles.avatar}>{client.display_name[0]}</div>
                  <div>
                    <ClientNameBlock displayName={client.display_name} fullName={client.full_name} nameStyle={styles.clientName} />
                    {meta && <div style={styles.clientMeta}>{meta}</div>}
                  </div>
                </div>
                {client.hero_oneliner && (
                  <div style={styles.cardBody}>{client.hero_oneliner}</div>
                )}
                <div style={styles.lastRow}>
                  <span style={styles.openArrow}>Open →</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const tb = {
  bar:       { background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)", padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  wordmark:  { fontWeight: "var(--weight-medium)", fontSize: 17, letterSpacing: "-0.3px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: 6 },
  dot:       { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--color-accent)" },
  right:     { display: "flex", alignItems: "center", gap: 16 },
  chip:      { display: "flex", alignItems: "center", gap: 10, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  avatar:    { width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 13 },
  logoutBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: 0 },
};

const styles = {
  page:       { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  container:  { maxWidth: 860, margin: "0 auto", padding: "48px 32px 80px" },
  heading:    { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-medium)", letterSpacing: "-0.5px", color: "var(--color-text-primary)" },
  subtitle:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginTop: 4, marginBottom: 28 },
  empty:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 32px", textAlign: "center", marginTop: 28 },
  emptyTitle: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", marginBottom: 6 },
  emptyBody:  { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", lineHeight: "var(--leading-normal)", maxWidth: 380, margin: "0 auto" },
  grid:       { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 },
  card:       { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: 28, textDecoration: "none", color: "var(--color-text-primary)", display: "block" },
  cardHead:   { display: "flex", alignItems: "center", gap: 16, marginBottom: 14 },
  avatar:     { width: 56, height: 56, borderRadius: "50%", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 20, flexShrink: 0 },
  clientName: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  clientMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 3 },
  cardBody:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", paddingTop: 14, borderTop: "1px solid var(--color-border)" },
  lastRow:    { marginTop: 14, fontSize: "var(--text-xs)", display: "flex", justifyContent: "flex-end" },
  openArrow:  { color: "var(--color-accent)", fontWeight: "var(--weight-medium)", fontSize: "var(--text-sm)" },
};
